import { string } from 'prop-types';
import { last, path, propOr } from 'ramda';
import React, { FC, useCallback, useState } from 'react';
import { compose, setDisplayName, setPropTypes, withHandlers } from 'recompose';
import { ExternalSource } from '../../../../constants/Source';
import { withFolderPath } from '../../../../utils/hoc/withFolderPath';
import { withItemsLoader } from '../../../../utils/hoc/withItemsLoader';
import { withItemsReducer } from '../../../../utils/hoc/withItemsReducer';
import { withMediaManagerSDK } from '../../../../utils/hoc/withMediaManagerSDK';
import { withMediaTypes } from '../../../../utils/hoc/withMediaTypes';
import { withSources } from '../../../../utils/hoc/withSources';
import { IMediaManagerSDKRequestOptions, MediaManagerSDKFolder } from '../../../../utils/media-manager-sdk';
import { decodeWixSiteFolderId, isSiteFilesRootFolderId } from '../../../../utils/media-manager-sdk/items';
import { ExternalSourcesConfigs } from '../../constants/Sources';
import { IInner, IOuter } from './Connected.types';
import { SORTING_OPTIONS, Sort } from '../../../../constants/Sort';
import { Layout } from '../../../../constants/Layout';
import { onHotKeys } from '../../../../utils/dom/domEvents';
import { Key } from 'ts-keycode-enum';
import withLifecycle from '@hocs/with-lifecycle';
import { JumpTo } from '../../../../utils/hoc/withItemsReducer/withItemsReducer.types';
import { withMediaManagerApi } from '../../../../utils/hoc/withMediaManagerApi';

export const withPropsForFolderPath = (BaseComponent) => {
  const WithPropsForFolderPath: FC<{ source: ExternalSource; folderPath: any }> = (props) => {
    const { folderPath } = props;
    const lastFolderId = folderPath ? propOr(null, 'id', last(folderPath)) : null;
    const [siteFolder, setSiteFolder] = useState(folderPath && folderPath[1]);
    const [folderId, setFolderId] = useState(lastFolderId);
    const [searchQuery, setSearchQuery] = useState('');
    const clearSearch = useCallback(() => setSearchQuery(''), []);
    const [siteSorting, setSiteSorting] = useState<Sort>(Sort.LastModified);
    const [siteLayout, setSiteLayout] = useState<Layout>(Layout.Gallery);

    return (
      <BaseComponent
        {...props}
        {...{
          folderId,
          siteFolder,
          setSiteFolder,
          searchQuery,
          setSearchQuery,
          clearSearch,
          siteSorting,
          setSiteSorting,
          siteLayout,
          setSiteLayout,
        }}
        navigateToFolder={setFolderId}
      />
    );
  };

  return WithPropsForFolderPath;
};

export const enhance = compose<IInner, IOuter>(
  setPropTypes({
    fileType: string.isRequired,
  }),
  setDisplayName('ConnectedEnhancers'),
  withMediaTypes,
  withSources,
  withPropsForFolderPath,
  withFolderPath({
    validateFolderIdIfCanGetFolderInfo: (folderId, props) => {
      const siteFolder = propOr<any, MediaManagerSDKFolder>(null, 'siteFolder', props);
      const decodedFolderId = decodeWixSiteFolderId(folderId);
      const isItSiteRootFolder = decodedFolderId && isSiteFilesRootFolderId(decodedFolderId.id);

      return { isValid: !isItSiteRootFolder, fallbackFolder: isItSiteRootFolder ? siteFolder : null };
    },
  }),
  withMediaManagerSDK(),
  withMediaManagerApi,
  withItemsLoader(
    ({ source, dynamicLoaderOptions, searchQuery, isWixSitesSource, mediaType, siteSorting }) => ({
      loaderOptions: {
        siteSorting,
        searchQuery,
        ...path<IMediaManagerSDKRequestOptions>([source, 'loaderOptions'], ExternalSourcesConfigs),
        ...(isWixSitesSource ? { mediaType, order: SORTING_OPTIONS[siteSorting] } : {}),
        ...dynamicLoaderOptions,
      },
    }),
    ['source', 'dynamicLoaderOptions', 'searchQuery', 'isWixSitesSource', 'mediaType', 'siteSorting'],
  ),
  withItemsReducer,
  withHandlers({
    handleJumpToNextItem:
      ({ jumpToNextItem }) =>
      ({ jumpTo }) =>
      (event) => {
        jumpToNextItem({ withShift: Boolean(event && event.shiftKey), jumpTo });
      },
  }),
  withHandlers({
    selectAllItemsHotKey: ({ selectAllItems }) => onHotKeys(`${Key.Ctrl}+${Key.A}`, selectAllItems, true),
    jumpToNextItemHotKey: ({ handleJumpToNextItem }) =>
      onHotKeys(`${Key.Shift}+${Key.RightArrow}|${Key.RightArrow}`, handleJumpToNextItem({ jumpTo: JumpTo.Next })),

    jumpToPrevItemHotKey: ({ handleJumpToNextItem }) =>
      onHotKeys(`${Key.Shift}+${Key.LeftArrow}|${Key.LeftArrow}`, handleJumpToNextItem({ jumpTo: JumpTo.Prev })),

    jumpToBottomItemHotKey: ({ handleJumpToNextItem }) =>
      onHotKeys(`${Key.Shift}+${Key.DownArrow}|${Key.DownArrow}`, handleJumpToNextItem({ jumpTo: JumpTo.Bottom })),

    jumpToTopItemHotKey: ({ handleJumpToNextItem }) =>
      onHotKeys(`${Key.Shift}+${Key.UpArrow}|${Key.UpArrow}`, handleJumpToNextItem({ jumpTo: JumpTo.Top })),
  }),
  withLifecycle(() => {
    return {
      onDidMount: ({
        selectAllItemsHotKey,
        jumpToNextItemHotKey,
        jumpToPrevItemHotKey,
        jumpToBottomItemHotKey,
        jumpToTopItemHotKey,
      }) => {
        window.addEventListener('keydown', selectAllItemsHotKey, false);
        window.addEventListener('keydown', jumpToNextItemHotKey, false);
        window.addEventListener('keydown', jumpToPrevItemHotKey, false);
        window.addEventListener('keydown', jumpToBottomItemHotKey, false);
        window.addEventListener('keydown', jumpToTopItemHotKey, false);
      },
      onWillUnmount: ({
        selectAllItemsHotKey,
        jumpToNextItemHotKey,
        jumpToPrevItemHotKey,
        jumpToBottomItemHotKey,
        jumpToTopItemHotKey,
      }) => {
        window.removeEventListener('keydown', selectAllItemsHotKey, false);
        window.removeEventListener('keydown', jumpToNextItemHotKey, false);
        window.removeEventListener('keydown', jumpToPrevItemHotKey, false);
        window.removeEventListener('keydown', jumpToBottomItemHotKey, false);
        window.removeEventListener('keydown', jumpToTopItemHotKey, false);
      },
    };
  }),
);
